import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import clsx from 'clsx'
import {
  Container,
  Grid,
  Button,
  Tabs,
  Tab,
  Box,
  Menu,
  MenuItem,
  makeStyles,
  useTheme
} from '@material-ui/core'
import {
  FormProvider,
  useForm
} from 'react-hook-form'

import { useAppContext } from '../../../../redux/slices/appContext'

import Icon from '../../../atoms/Icon'
import Text from '../../../atoms/Text'
import RoundedButton from '../../../atoms/RoundedButton'
import PrintViewModestyCurtain from '../../../molecules/PrintViewModestyCurtain'
import Dialog from '../../../molecules/Dialog/Dialog'
import SnackAlert from '../../../molecules/SnackAlert/SnackAlert'
import { useOperationalTable } from '../../../organisms/OperationalTable'

import {
  ICON_NAMES,
  ADMIN_ROUTES,
  BUTTON_SIZES,
  SUCCESS_ALERT,
  OKTA_USER_STATUS
} from '../../../../constants'
import { useClients } from '../Clients/ClientListTab/hooks'
import { useInvalidateClientsQueries } from '../../../../api/clients'
import {
  useRemoveUser,
  useResetUser,
  useInvalidateUsersQueries
} from '../../../../api/users'
import UserForm from './UserForm'
import ClientsTable from './ClientsTable'
import AssociatedClientsTable from './AssociatedClientsTable'
import Remove from './Remove'

import { columnsConfig } from './columns'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '1rem',
    height: '100%',
    [theme.breakpoints.down('xs')]: {
      padding: '1.75rem'
    }
  },
  pillBtn: {
    border: `1.5px solid ${theme.palette.summitBlue}`,
    color: theme.palette.darkJungle,
    borderRadius: 25,
    padding: '4px 16px 3px 16px',
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '0.75rem'
  },
  pillEdit: {
    padding: '0.5rem 1.625rem'
  },
  pillEditing: {
    backgroundColor: theme.palette.culture,
    color: theme.palette.romanSilver,
    border: `1.5px solid ${theme.palette.culture}`
  },
  flexItem: {
    display: 'flex',
    alignItems: 'center'
  },
  tabs: {
    width: 'calc(100% + (2.75rem * 2))',
    borderBottom: '2px solid #EEF0F8',
    left: '-2.75rem',
    marginTop: '1.5rem',
    position: 'relative',
    '& > .MuiTabs-fixed': {
      left: '2.75rem'
    },
    [theme.breakpoints.down('md')]: {
      width: 'calc(100% + (10rem * 2))',
      left: '-10rem',
      '& > .MuiTabs-fixed': {
        left: '10rem'
      }
    }
  },
  tab: {
    color: theme.palette.dimGray,
    fontSize: '1.25rem',
    textTransform: 'none',
    '&.Mui-selected': {
      color: theme.palette.darkJungle,
      fontWeight: 'bold'
    }
  },
  tabsContainer: {
    padding: '1.5rem 1.7rem',
    [theme.breakpoints.down('md')]: {
      padding: '1.5rem 0'
    }
  },
  removeBtn: {
    backgroundColor: theme.palette.palePink,
    color: theme.palette.error.primary,
    '&:hover': {
      backgroundColor: theme.palette.error.primary,
      color: theme.palette.palePink
    }
  },
  addUser: {
    '&:hover': {
      cursor: 'pointer'
    }
  }
}))

const CLIENTS_PAGE_SIZE = 50
// const ADVISOR = 'advisor'

function User () {
  const { isAdvisor, isSummitUser } = useAppContext()
  const { userId } = useParams()
  const classes = useStyles()
  const theme = useTheme()
  const history = useHistory()

  const isNew = userId === 'new'

  const [editingUser, setEditingUser] = useState(isNew)
  const [tab, setTab] = useState(0)
  const [alert, setAlert] = useState({})

  const [associatedClients, setAssociatedClients] = useState([])
  const [addAssociations, setAddAssociations] = useState([])
  const [removeAssociations, setRemoveAssociations] = useState([])
  const [selectedAssociatedClients, setSelectedAssociatedClients] = useState([])
  const [isLoadingUser, setIsLoadingUser] = useState(true)
  const [openDeactivateUser, setOpenDeactivateUser] = useState(false)
  const [moreEl, setMoreEl] = useState(null)

  const invalidateQueries = useInvalidateUsersQueries()
  const resetClientsQueries = useInvalidateClientsQueries()

  const initialFormState = useMemo(() => ({
    firstName: '',
    lastName: '',
    email: '',
    internalId: '',
    userType: 'advisor',
    allAccountsAccess: false,
    internal: true
  }), [])

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: initialFormState
  })

  const { getValues, watch } = formMethods
  const allAccountsAccess = watch('allAccountsAccess')

  function onSuccesUsers ({ data }) {
    setAssociatedClients(data)
    setSelectedAssociatedClients(data?.map((item) => item.clientId))
  }

  function onSuccessRemoveUser () {
    invalidateQueries()
    history.push(ADMIN_ROUTES.USERS)
  }

  function onSuccessResetUser () {
    setAlert({ ...SUCCESS_ALERT, alertMessage: 'Email with instructions has been sent' })
    invalidateQueries()
  }

  const { columns, defaultSort } = columnsConfig

  // Clients table
  const {
    defaultPageSize,
    pageIndex,
    pageSize,
    sort,
    searchText,
    onPagingChange,
    onSortingChange,
    onSearchTextChange,
    onTableModeChange,
    tableMode
  } = useOperationalTable({ defaultSort, defaultPageSize: CLIENTS_PAGE_SIZE })

  // Asssociated clients table
  const {
    defaultPageSize: aDefaultPageSize,
    pageIndex: aPageIndex,
    pageSize: aPageSize,
    sort: aSort,
    onPagingChange: aOnPagingChange,
    onSortingChange: aOnSortingChange,
    onTableModeChange: aOnTableModeChange,
    tableMode: aTableMode
  } = useOperationalTable({ defaultSort, defaultPageSize: CLIENTS_PAGE_SIZE })

  // Get clients data
  const { data: clients, loading: isLoadingClients } = useClients({
    pageIndex,
    pageSize,
    sort,
    searchText,
    tableMode
  })

  // Get associated clients to the user
  const {
    data: fetchAssociatedClients,
    loading: loadingAssociated,
    refetch,
    isRefetching
  } = useClients({
    aPageIndex,
    aPageSize,
    aSort,
    aTableMode,
    filters: {
      assignToUserIds: { op: 'in', value: getValues('internalId') }
    },
    onSuccess: onSuccesUsers
  })

  const {
    mutateAsync: removeUser,
    isLoading: isRemovingUser
  } = useRemoveUser(getValues('userId'), onSuccessRemoveUser)

  const {
    mutateAsync: resetUser,
    isLoading: isResettingUser
  } = useResetUser({ email: getValues('email') }, onSuccessResetUser)

  const onRemoveAssociation = (clientId) => () => {
    setAssociatedClients(prev => [...prev].filter((item) => item.clientId !== clientId))
    setRemoveAssociations(prev => [...prev, clientId])
  }

  const onAddAssociation = (clientId) => () => {
    const client = clients.find(item => item.clientId === clientId)

    if (client) {
      setAssociatedClients([...associatedClients, client])
      setAddAssociations([...addAssociations, client])
    }
  }

  const clientsColumns = [...columns, {
    Header: 'Actions',
    accessor: (row) => {
      const associatedClient = associatedClients.find(({ clientId }) => clientId === row.clientId)

      return (
        associatedClient ? (
          <Remove onClick={onRemoveAssociation(row.clientId)}>
            <Remove.NoHover>
              <Icon name={ICON_NAMES.circleMinus} />
            </Remove.NoHover>
            <Remove.Hover>
              <Icon name={ICON_NAMES.circleMinusHover} />
            </Remove.Hover>
          </Remove>
        ) : (
          <Box onClick={onAddAssociation(row.clientId)} className={classes.addUser}>
            <Icon
              name={ICON_NAMES.addCircle}
              customSize='1rem'
            />
          </Box>
        )
      )
    },
    id: 'actions',
    disableSortBy: true
  }]

  const handleTabChange = useCallback((_, tabIndex) => setTab(tabIndex), [])
  const openMoreMenu = useCallback((event) => setMoreEl(event.currentTarget), [])
  const closeMoreMenu = useCallback(() => setMoreEl(null), [])
  const closeDialog = useCallback(() => setOpenDeactivateUser(false), [])
  const openDeactivate = useCallback(() => {
    setOpenDeactivateUser(true)
    closeMoreMenu()
  }, [closeMoreMenu])

  const onClickReset = useCallback(() => {
    resetUser()
    closeMoreMenu()
  }, [resetUser, closeMoreMenu])

  const clientsProps = {
    onSearchTextChange,
    onSortingChange,
    onPagingChange,
    onTableModeChange,
    defaultPageSize,
    searchText,
    loading: isLoadingClients,
    columns: clientsColumns,
    clients
  }

  const associatedClientsProps = {
    onSortingChange: aOnSortingChange,
    onPagingChange: aOnPagingChange,
    onTableModeChange: aOnTableModeChange,
    defaultPageSize: aDefaultPageSize,
    loading: loadingAssociated,
    clients: associatedClients,
    columns
  }

  const isDisableRemove = (!isAdvisor && !getValues('currentInternal')) || (getValues('currentInternalId') === getValues('internalId'))
  const showResetUser = isSummitUser && !isDisableRemove && getValues('userStatus') === OKTA_USER_STATUS.active

  useEffect(() => {
    if (isRefetching) {
      setEditingUser(false)
    }
  }, [isRefetching])

  useEffect(() => {
    if (allAccountsAccess) {
      setTab(0)
    }
  }, [allAccountsAccess])

  useEffect(() => {
    return () => {
      resetClientsQueries()
    }
  }, [resetClientsQueries])

  return (
    <>
      <PrintViewModestyCurtain
        text={isRemovingUser ? 'Deleting User' : 'Resetting User'}
        show={isRemovingUser || isResettingUser}
      />
      <Container maxWidth={false} className={classes.container}>
        <FormProvider {...formMethods}>
          {
            !isNew && (
              <Grid
                container
                justifyContent='flex-end'
                className={classes.flexItem}
                spacing={1}
              >
                <Grid item>
                  <Button
                    className={clsx([
                      classes.pillBtn,
                      classes.pillEdit
                    ], {
                      [classes.pillEditing]: editingUser
                    })}
                    variant='outlined'
                    onClick={() => setEditingUser(!editingUser)}
                    disabled={isLoadingUser || isResettingUser}
                  >
                    {editingUser ? 'Editing' : 'Edit'}
                  </Button>
                </Grid>
                {
                  getValues('internalId') && (
                    <Grid item>
                      <Button aria-controls='more-opts' aria-haspopup='true' onClick={openMoreMenu}>
                        <Icon name={ICON_NAMES.threeDots} customSize='1.5rem' />
                      </Button>
                      <Menu
                        id='more-opts'
                        anchorEl={moreEl}
                        keepMounted
                        open={Boolean(moreEl)}
                        onClose={closeMoreMenu}
                      >
                        {
                          showResetUser && (
                            <MenuItem onClick={onClickReset}>
                              <Box display='flex' alignItems='center' gridGap='0.75rem'>
                                <Icon
                                  name={ICON_NAMES.resetUser}
                                  customSize='1.5rem'
                                />
                                <Text>Reset User</Text>
                              </Box>
                            </MenuItem>
                          )
                        }
                        <MenuItem onClick={openDeactivate}>
                          <Box display='flex' alignItems='center' gridGap='0.75rem'>
                            <></>
                            <Icon
                              name={ICON_NAMES.deactivateUser}
                              customSize='1.5rem'
                            />
                            <Text>Deactivate User</Text>
                          </Box>
                        </MenuItem>
                      </Menu>
                    </Grid>
                  )
                }
              </Grid>
            )
          }
          <UserForm
            fetchAssociatedClients={fetchAssociatedClients}
            associatedClients={associatedClients}
            addAssociations={addAssociations}
            removeAssociations={removeAssociations}
            refetchAssociated={refetch}
            selectedAssociatedClients={selectedAssociatedClients}
            editingUser={editingUser}
            setIsLoadingUser={setIsLoadingUser}
          />
          {
            !allAccountsAccess && (
              <>
                <Tabs
                  fullWidth
                  value={tab}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: theme.palette.darkJungle,
                      height: '0.25rem'
                    }
                  }}
                  className={classes.tabs}
                >
                  <Tab label='Clients' className={classes.tab} disabled={allAccountsAccess} />
                </Tabs>
                <Grid container className={classes.tabsContainer}>
                  <Grid container>
                    {
                      editingUser ? (
                        <ClientsTable {...clientsProps} />
                      ) : (
                        <AssociatedClientsTable {...associatedClientsProps} />
                      )
                    }
                  </Grid>
                </Grid>
              </>
            )
          }
        </FormProvider>
      </Container>
      <Dialog open={openDeactivateUser} onClose={closeDialog}>
        <Dialog.Header>
          <Text
            text='Deactivate User'
            color={theme.palette.summitBlue}
            customFontSize='1.5rem'
            customFontWeight='900'
          />
        </Dialog.Header>
        <Dialog.Body>
          <Text color={theme.palette.summitBlue} customFontSize='0.875rem'>
            Are you sure you want to deactivate {getValues('email')}?
          </Text>
        </Dialog.Body>
        <Dialog.Actions className={classes.modalActions}>
          <Grid container spacing={4} justifyContent='center'>
            <Grid item lg={6} xs={12}>
              <RoundedButton
                fullWidth
                size={BUTTON_SIZES.medium}
                onClick={closeDialog}
              >
                Cancel
              </RoundedButton>
            </Grid>
            <Grid item lg={6} xs={12}>
              <RoundedButton
                fullWidth
                size={BUTTON_SIZES.medium}
                onClick={removeUser}
                className={classes.removeBtn}
              >
                Yes, deactivate
              </RoundedButton>
            </Grid>
          </Grid>
        </Dialog.Actions>
      </Dialog>
      <SnackAlert alert={alert} />
    </>
  )
}

export default User
